<template>
    <app-main>
        <app-content>

            <div class="flex flex-wrap -mx-2">
                <div class="w-full md:w-1/2 px-2">
                    <!-- Start Title -->
                    <app-title
                        title="Pages"
                    />
                    <!-- End Title -->
                </div>
                <div class="w-full md:w-1/2 px-2 md:text-right">
                    <app-button element="button" @click="$router.push({ name: 'pages.new' })" class="btn btn--primary btn--primary--sml" v-if="checkPermission('pages', 'create')">New Page</app-button>
                </div>
            </div>

            <app-box :loading="loading" :initial="loading" v-if="checkPermission('pages', 'view')">
                <div class="flex flex-wrap -mx-2">
                    <div class="flex w-full px-2">
                        <app-field-text
                            v-model="search"
                            placeholder="Search Pages"
                            class="w-full mr-4"
                            @input="searchPages"
                        />
                        <app-button
                            element="button"
                            @click="resetSearch"
                            :class="['flex whitespace-no-wrap items-center align-center btn btn--primary btn--primary--sml mb-5']"
                        >
                            Reset Search
                        </app-button>
                    </div>
                </div>

                <app-table v-if="pages.length > 0">
                    <app-table-header>
                        <app-table-row :header="true">
                            <app-table-cell :header="true">
                                Title
                            </app-table-cell>
                            <app-table-cell class="text-center" :header="true">
                                Published
                            </app-table-cell>
                            <app-table-cell class="text-center" :header="true">
                                Published At
                            </app-table-cell>
                            <app-table-cell class="text-center" :header="true" />
                        </app-table-row>
                    </app-table-header>

                    <app-table-body>
                        <app-table-row v-for="(p, i) in pages" :key="i">
                            <app-table-cell>
                                {{ p.title }}
                                <a :href="`${app_url}/${p.slug}`" target="_blank" class="text-xs block mt-1 text-blue">{{ app_url }}/{{ p.slug }}</a>
                            </app-table-cell>
                            <app-table-cell class="text-center">
                                <app-icon :icon="p.published ? 'check-circle' : 'times-circle'" :class="{'text-green': p.published, 'text-red': !p.published, 'text-lg': true}" />
                            </app-table-cell>
                            <app-table-cell class="text-center">
                                {{ moment(p.published_at).format('D MMM YY') }}
                                <span class="text-xs block mt-1 text-grey">({{ moment(p.published_at).fromNow() }})</span>
                            </app-table-cell>
                            <app-table-cell>
                                <router-link :to="{ name: 'pages.edit', params: { id: p.id, page: $route.query.page ? $route.query.page : 1, search: $route.query.search ? $route.query.search : '' } }" class="mr-2 text-blue hover:text-blue-darker"  v-if="checkPermission('pages', 'edit') && p.children.length < 1">
                                    <app-icon icon="edit" />
                                </router-link>
                                <app-icon icon="trash" class="text-red cursor-pointer" @click="confirmModalOpened = true; confirmModalId = p.id" v-if="checkPermission('pages', 'delete')" />
                            </app-table-cell>
                        </app-table-row>
                    </app-table-body>
                </app-table>

                <app-pagination-links
                    :response="response"
                    :resources="pages"
                    @next="selectNextPage"
                    @previous="selectPreviousPage"
                />
            </app-box>
        </app-content>

        <app-confirm-modal
            v-if="confirmModalOpened"
            @confirm="remove"
            @close="confirmModalOpened = false; confirmModalId = false"
            actionText="Yes please"
            cancelText="No thanks"
            heading="Hang on!"
            text="You are about to delete a page. Are you sure you wish to continue?"
        />
    </app-main>
</template>

<script>
    import _ from 'lodash';
    import { mapGetters } from 'vuex';
    /* API */
    import Api from '@/api/index.js';
    import Auth from '@/api/auth.js';
    /* Components */
    import Main from '@/components/Main';
    import Content from '@/components/template/Content';
    import Title from '@/components/Title';
    import Box from '@/components/Box';
    import Button from '@/components/Button';
    import PaginationLinks from '@/components/PaginationLinks';
    import ConfirmModal from '@/components/modals/ConfirmModal';
    import TextField from '@/components/fields/inputs/Text';

    /* Mixins */
    import Query from '@/mixins/query';
    import Paginatable from '@/mixins/paginatable';
    import Permissions from '@/mixins/permissions';

    import {
        APP_URL
    } from '@/constants/default.js';

    export default {
        name: 'pages.home',

        data: () => ({
            loading: true,
            pages: [],
            response: null,
            app_url: '',
            confirmModalOpened: false,
            confirmModalId: false,
            search: ''
        }),

        mixins: [ Query, Paginatable, Permissions ],

        components: {
            'app-main': Main,
            'app-content': Content,
            'app-title': Title,
            'app-box': Box,
            'app-button': Button,
            'app-pagination-links': PaginationLinks,
            'app-confirm-modal': ConfirmModal,
            'app-field-text': TextField,
        },

        created() {
            if (this.$route.query.search && this.$route.query.search !== '') {
                this.search = this.$route.query.search;
            }
        },

        mounted() {
            this.app_url = APP_URL;

            this.getPages()
                .then(response => {
                    this.loading = false;
                });
        },

        methods: {
            searchPages: _.debounce(function() {
                this.$route.query.page = '1';
                return this.getPages();
            }, 500),

            resetSearch() {
                this.search = '';
                this.$route.query.search = '';
                this.$route.query.page = '1';
                this.getPages();
            },

            getPages() {
                let params = {};

                if (this.$route.query.page) {
                    params.page = this.$route.query.page;
                }

                if (this.search !== '') {
                    this.$route.query.search = this.search;
                    params.search = this.search;
                }

                return Api.request('get', `pages?${this.prepareGetQueryString(params)}`)
                    .then(({ data }) => {
                        this.pages    = data.pages;
                        this.response = data;
                    });
            },

            remove() {
                this.confirmModalOpened = false;
                this.loading = true;

                this.deletePage( this.confirmModalId ).then(response => {
                    this.getPages()
                        .then(response => {
                            this.loading = false;
                            this.confirmModalId = false;
                        });
                });
            },

            deletePage(id) {
                return Api.request('get', `pages/delete/${id}`);
            },

            isTranslated(page, locale) {
                let translated = false;

                if (page.children.length > 0 && page.locale.flag != locale.flag) {
                    page.children.forEach(child => {
                        if (child.locale.flag == locale.flag) {
                            translated = true;
                        }
                    });
                }

                return translated;
            },

            getId(page, locale) {
                let id = page.id;

                if (page.children.length > 0 && page.locale.flag != locale.flag) {
                    page.children.forEach(child => {
                        if (child.locale.flag == locale.flag) {
                            id = child.id;
                        }
                    });
                }

                return id;
            }
        },

        computed: {
            ...mapGetters({
                locales: 'locales/items'
            })
        },
    }
</script>



// WEBPACK FOOTER //
// src/views/pages/Home.vue